import { ReloadOutlined } from '@ant-design/icons';
import type { DeliveryRow, DeliveryStatus } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Segmented, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { SlaBadge } from '../components/SlaBadge';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

type StatusFilter = 'ALL' | DeliveryStatus;

const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待发货', color: 'default' },
  PICKING: { text: '拣货中', color: 'processing' },
  PACKED: { text: '已打包', color: 'processing' },
  SHIPPED: { text: '已发货', color: 'blue' },
  DELIVERING: { text: '配送中', color: 'blue' },
  ARRIVED: { text: '已到达', color: 'cyan' },
  SIGNED: { text: '已签收', color: 'green' },
  PART_SIGNED: { text: '部分签收', color: 'orange' },
  REJECTED: { text: '已拒收', color: 'red' },
  LOST: { text: '丢失', color: 'red' },
  CANCELED: { text: '已取消', color: 'default' }
};

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '配送中', value: 'DELIVERING' },
  { label: '已签收', value: 'SIGNED' },
  { label: '部分签收', value: 'PART_SIGNED' },
  { label: '已拒收', value: 'REJECTED' }
];

export default function DeliveryList() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>('ALL');

  const query = useInfiniteQuery({
    queryKey: ['delivery', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.delivery.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        deliveryStatus: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<DeliveryRow> = [
    {
      title: '配送单号',
      dataIndex: 'bizNo',
      width: 230,
      render: (value: string, row) => <Typography.Link onClick={() => navigate(`/delivery/${row.id}`)}>{value}</Typography.Link>
    },
    { title: '收货科室', dataIndex: 'toDeptName', width: 160, render: (value: string | null) => value || '-' },
    { title: '配送地址', dataIndex: 'toSpaceDesc', ellipsis: { showTitle: false }, render: ellipsisCell('toSpaceDesc') },
    { title: '配送人', dataIndex: 'deliverName', width: 110, render: (value: string | null) => value || '-' },
    {
      title: '状态',
      dataIndex: 'deliveryStatus',
      width: 120,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '时效',
      dataIndex: 'planTime',
      width: 150,
      render: (value: string | null, row) => (
        <SlaBadge
          deadline={value}
          finished={['SIGNED', 'REJECTED', 'CANCELED', 'LOST'].includes(row.deliveryStatus)}
          finishedText={row.deliveryStatus === 'SIGNED' ? '已送达' : undefined}
        />
      )
    },
    { title: '签收时间', dataIndex: 'signAt', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Button type="link" onClick={() => navigate(`/delivery/${row.id}`)}>
          详情
        </Button>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title="配送签收"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Segmented<StatusFilter>
        value={status}
        options={FILTERS}
        onChange={(value) => setStatus(value)}
        style={{ marginBottom: 12 }}
      />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="配送单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<DeliveryRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无配送单' }}
        onRow={(row) => ({ onDoubleClick: () => navigate(`/delivery/${row.id}`) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : rows.length > 0 ? (
          <Typography.Text type="secondary">没有更多了</Typography.Text>
        ) : null}
      </Space>
    </Card>
  );
}
